// src/ConsumerApp/Screens/Account/components/RaiseIssueModal.js

import React, { useState, useEffect } from 'react';
import { useGetIssueReasons } from '../../../api/orders/orders.hooks';
import X from '../../../../assets/icons/X.svg';

export default function RaiseIssueModal({
  isOpen,
  onClose,
  onSubmitIssue,
  isSubmitting = false,
  orderNumber = '',
}) {
  const [selectedReasonCode, setSelectedReasonCode] = useState('');
  const [description, setDescription] = useState('');

  const { data: reasons = [], isLoading: isReasonsLoading } = useGetIssueReasons('issues');
  //console.log(reasons);

  useEffect(() => {
    if (isOpen) {
      setSelectedReasonCode('');
      setDescription('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelectReason = (code) => {
    setSelectedReasonCode(code);
  };

  const handleProceed = () => {
    if (!selectedReasonCode || isSubmitting) return;

    const matchedReason = reasons.find((r) => r.code === selectedReasonCode);
    const payload = {
      reason_code: selectedReasonCode,
      reason_label: matchedReason?.label || selectedReasonCode,
      description: description.trim(),
    };

    if (onSubmitIssue) {
      onSubmitIssue(payload);
    }
  };

  return (
    <>
      <style>{`
        .bb-issue-modal-overlay {
          position: fixed;
          inset: 0;
          background-color: rgba(15, 23, 42, 0.6);
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(3px);
          padding: 16px;
          box-sizing: border-box;
          animation: bbFadeIn 0.15s ease-out;
        }

        @keyframes bbFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .bb-issue-sheet-card {
          background-color: #FFFFFF;
          border-radius: 16px;
          padding: 24px;
          width: 100%;
          max-width: 480px;
          max-height: 90vh;
          box-shadow: 0 16px 40px rgba(15, 23, 42, 0.2);
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
          animation: bbScaleUp 0.18s cubic-bezier(0.16, 1, 0.3, 1);
          overflow: hidden;
        }

        @keyframes bbScaleUp {
          from { transform: scale(0.96); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .bb-issue-scrollable-body {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding-right: 4px;
          margin-top: 14px;
          margin-bottom: 16px;
        }

        .bb-reasons-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .bb-reason-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-radius: 10px;
          border: 1.5px solid #E2E8F0;
          background-color: #FFFFFF;
          cursor: pointer;
          transition: all 0.15s ease;
          user-select: none;
        }

        .bb-reason-item:hover {
          border-color: #CBD5E1;
          background-color: #F8FAFC;
        }

        .bb-reason-item-selected {
          border-color: #E9001E !important;
          background-color: #FFF5F5 !important;
        }

        .bb-radio-indicator {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1.5px solid #94A3B8;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: border-color 0.15s ease;
        }

        .bb-radio-indicator-selected {
          border-color: #E9001E;
        }

        .bb-radio-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #E9001E;
        }

        .bb-issue-textarea {
          width: 100%;
          height: 88px;
          border-radius: 10px;
          border: 1.5px solid #CBD5E1;
          padding: 10px 12px;
          font-size: 13.5px;
          color: #0F172A;
          outline: none;
          resize: none;
          font-family: inherit;
          box-sizing: border-box;
          background-color: #F8FAFC;
          transition: border-color 0.15s ease, background-color 0.15s ease;
        }

        .bb-issue-textarea:focus {
          border-color: #E9001E;
          background-color: #FFFFFF;
        }

        .bb-issue-actions-row {
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: flex-end;
          width: 100%;
          padding-top: 10px;
          border-top: 1px solid #F1F5F9;
        }

        @media (max-width: 640px) {
          .bb-issue-modal-overlay {
            align-items: flex-end !important;
            padding: 0 !important;
          }

          .bb-issue-sheet-card {
            max-width: 100% !important;
            max-height: 85vh !important;
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
            border-top-left-radius: 20px !important;
            border-top-right-radius: 20px !important;
            padding: 20px 16px !important;
            padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px)) !important;
            animation: bbSlideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1) !important;
          }

          @keyframes bbSlideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }

          .bb-issue-actions-row {
            flex-direction: column-reverse !important;
            gap: 8px !important;
          }

          .bb-issue-actions-row button {
            width: 100% !important;
            height: 44px !important;
            font-size: 14px !important;
          }
        }
      `}</style>

      <div className="bb-issue-modal-overlay" onClick={onClose}>
        <div className="bb-issue-sheet-card" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
            <div>
              <h4 style={{ margin: 0, fontSize: 18, fontWeight: '800', color: '#0F172A' }}>
                Raise an Issue
              </h4>
              <span style={{ fontSize: 13, color: '#64748B', display: 'block', marginTop: 4 }}>
                {orderNumber ? `Order #${orderNumber}` : 'Select a reason for issue'}
              </span>
            </div>
            <button
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              onClick={onClose}
              disabled={isSubmitting}
              type="button"
              aria-label="Close"
            >
              <img src={X} alt="close" style={{ width: 14, height: 14, opacity: 0.6 }} />
            </button>
          </div>

          {/* Scrollable Form Body */}
          <div className="bb-issue-scrollable-body">
            <div>
              <label style={{ fontSize: 12.5, fontWeight: '700', color: '#334155', display: 'block', marginBottom: 8 }}>
                Reason for Issue <span style={{ color: '#E9001E' }}>*</span>
              </label>

              {isReasonsLoading ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#707B8C', fontSize: 13 }}>
                  Loading return reasons...
                </div>
              ) : (
                <div className="bb-reasons-list">
                  {reasons.map((reason) => {
                    const isSelected = selectedReasonCode === reason.code;
                    return (
                      <div
                        key={reason.code}
                        className={`bb-reason-item ${isSelected ? 'bb-reason-item-selected' : ''}`}
                        onClick={() => handleSelectReason(reason.code)}
                      >
                        <div className={`bb-radio-indicator ${isSelected ? 'bb-radio-indicator-selected' : ''}`}>
                          {isSelected && <div className="bb-radio-dot" />}
                        </div>
                        <span style={{
                          fontSize: 13,
                          fontWeight: isSelected ? '700' : '500',
                          color: isSelected ? '#0F172A' : '#334155'
                        }}>
                          {reason.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ fontSize: 12.5, fontWeight: '700', color: '#334155' }}>
                  Additional Details <span style={{ color: '#94A3B8', fontWeight: '500' }}>(Optional)</span>
                </label>
                <span style={{ fontSize: 11, color: '#94A3B8' }}>
                  {description.length}/500
                </span>
              </div>
              <textarea
                className="bb-issue-textarea"
                placeholder="Describe the issue with the materials or delivery..."
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Actions Footer */}
          <div className="bb-issue-actions-row">
            <button
              type="button"
              style={styles.cancelBtn}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{
                ...styles.confirmBtn,
                opacity: !selectedReasonCode || isSubmitting ? 0.6 : 1,
                cursor: !selectedReasonCode || isSubmitting ? 'not-allowed' : 'pointer',
              }}
              onClick={handleProceed}
              disabled={!selectedReasonCode || isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

const styles = {
  cancelBtn: {
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: '#334155',
    padding: '9px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
  confirmBtn: {
    border: 'none',
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    padding: '9px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: '800',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(233, 0, 30, 0.25)',
  },
};